import "@components/ag-grid/styles/AgCharts.css";
import { GetUpiSuccessRateResponseData } from "@services/models/Analytics/GetUpiSuccessRate";
import { formatDisplayDate } from "@utils/safeDate";
import { AgCharts } from "ag-charts-react";
import React, { useMemo, useState } from "react";

type Props = {
  data: GetUpiSuccessRateResponseData[];
};

const predefinedColors = [
  "#3B4CDB",
  "#81D1F4",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#84CC16",
];

const getRandomColor = () =>
  `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, "0")}`;

const UpiSuccessRateChart: React.FC<Props> = ({ data }) => {
  const [selectedUpiId, setSelectedUpiId] = useState<string>("");

  const { upiIds, chartOptions } = useMemo(() => {
    if (!data || data.length === 0) {
      return { upiIds: [], chartOptions: {} };
    }

    const upiIds = [...new Set(data.map((d) => d.upiId))];
    const dates = [...new Set(data.map((d) => d.slot))].sort();
    const currentUpiId = selectedUpiId || upiIds[0];

    const upiEntries = data.filter((d) => d.upiId === currentUpiId);

    const chartData = dates.map((date) => {
      const dateFormatted = formatDisplayDate(date, "en-IN", {
        day: "numeric",
        month: "short",
      });

      const upiEntry = upiEntries.find((entry) => entry.slot === date);
      const successRate = upiEntry
        ? parseFloat(upiEntry.successRatePercent.toString())
        : 0;

      return {
        date: dateFormatted,
        slot: date,
        successRate,
      };
    });

    const color = predefinedColors[0] || getRandomColor();

    const options = {
      data: chartData,
      series: [
        {
          type: "bar",
          xKey: "date",
          yKey: "successRate",
          yName: "Success Rate (%)",
          fill: color,
          stroke: color,
          strokeWidth: 1,
        },
      ],
      axes: [
        {
          type: "category",
          position: "bottom",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
            rotation: 0,
          },
          line: {
            color: "transparent",
          },
          tick: {
            color: "transparent",
          },
          gridLine: {
            enabled: false,
          },
        },
        {
          type: "number",
          position: "left",
          title: {
            enabled: false,
          },
          label: {
            fontSize: 10,
            color: "var(--text-base)",
            formatter: (params: any) => `${params.value}%`,
          },
          line: {
            color: "transparent",
          },
          tick: {
            color: "transparent",
          },
          gridLine: {
            enabled: false,
          },
          min: 0,
        },
      ],
      background: {
        fill: "transparent",
      },
      padding: {
        top: 20,
        right: 20,
        bottom: 40,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        class: "ag-chart-tooltip-custom",
      },
    } as any;

    return { upiIds, chartOptions: options };
  }, [data, selectedUpiId]);

  if (!data || data.length === 0) return null;

  return (
    <div className="flex flex-row w-full h-full overflow-hidden">
      {/* Chart container */}
      <div className="flex-[1_1_0%] h-full overflow-hidden">
        <AgCharts options={chartOptions} />
      </div>

      {/* UPI Selector */}
      <div className="flex-shrink-0 w-[250px] p-4 border-l border-border-subtle bg-white">
        <span className="text-[12px] font-medium mb-2 block">
          Select UPI ID
        </span>
        <select
          id="upi-select"
          value={selectedUpiId || upiIds[0]}
          onChange={(e) => setSelectedUpiId(e.target.value)}
          className="w-full p-[8px] rounded-[6px] border border-black text-[12px]"
        >
          {upiIds.map((upi) => (
            <option key={upi} value={upi}>
              {upi}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default UpiSuccessRateChart;
